import {
  type OutboundParcel,
  type Parcel,
  ParcelSchema,
  ParcelType,
} from "@redotech/redo-fulfillment-parcel-common/parcel";
import * as z from "zod";

// --- Schemas ---

const PackingParcelSchema_ = z.object({
  id: z.string(),
  label: z.string(),
  parcel: ParcelSchema.nullish(),
});
export const PackingParcelSchema: z.ZodType<PackingParcel> =
  PackingParcelSchema_;
export interface PackingParcel extends z.infer<typeof PackingParcelSchema_> {}

const ItemParcelAssignmentSchema_ = z.object({
  parcelId: z.string(),
  quantity: z.number(),
});
export const ItemParcelAssignmentSchema: z.ZodType<ItemParcelAssignment> =
  ItemParcelAssignmentSchema_;
export interface ItemParcelAssignment extends z.infer<
  typeof ItemParcelAssignmentSchema_
> {}

export type ItemParcelAssignments = Record<string, ItemParcelAssignment[]>;

const PackingParcelsSchema_ = z.array(PackingParcelSchema_);
export const PackingParcelsSchema: z.ZodType<PackingParcel[]> =
  PackingParcelsSchema_;

const ItemParcelAssignmentsSchema_ = z.record(
  z.string(),
  z.array(ItemParcelAssignmentSchema_),
);
export const ItemParcelAssignmentsSchema: z.ZodType<ItemParcelAssignments> =
  ItemParcelAssignmentsSchema_;

// --- Pure helper functions ---

/**
 * Count total items assigned to a specific parcel across all line items.
 */
export function getParcelItemCount(
  parcelId: string,
  assignments: ItemParcelAssignments,
): number {
  let count = 0;
  for (const itemAssignments of Object.values(assignments)) {
    for (const assignment of itemAssignments) {
      if (assignment.parcelId === parcelId) {
        count += assignment.quantity;
      }
    }
  }
  return count;
}

/**
 * Auto-assign scanned items to the active parcel based on scan deltas.
 * Returns updated assignments and whether any changes were made.
 */
export function applyParcelAutoAssignments(
  currentAssignments: ItemParcelAssignments,
  activeParcelId: string,
  scanDeltas: { externalId: string; delta: number }[],
): { assignments: ItemParcelAssignments; changed: boolean } {
  const assignments = { ...currentAssignments };
  let changed = false;

  for (const { externalId, delta } of scanDeltas) {
    if (delta <= 0) {
      continue;
    }
    const existingAssignments = [...(assignments[externalId] ?? [])];
    const isInActiveParcel = existingAssignments.some(
      (a) => a.parcelId === activeParcelId,
    );

    if (isInActiveParcel) {
      // Already assigned to active parcel (possibly among others): increment it.
      assignments[externalId] = existingAssignments.map((a) =>
        a.parcelId === activeParcelId
          ? { ...a, quantity: a.quantity + delta }
          : a,
      );
    } else if (existingAssignments.length > 0) {
      // Assigned entirely to other parcel(s): move everything to active parcel.
      const totalQuantity = existingAssignments.reduce(
        (sum, a) => sum + a.quantity,
        0,
      );
      assignments[externalId] = [
        { parcelId: activeParcelId, quantity: totalQuantity },
      ];
    } else {
      // No existing assignment: assign the scanned delta.
      assignments[externalId] = [{ parcelId: activeParcelId, quantity: delta }];
    }
    changed = true;
  }

  return { assignments, changed };
}

/**
 * Remove all assignments for a given parcel, cleaning up empty entries.
 */
export function removeParcelFromAssignments(
  assignments: ItemParcelAssignments,
  parcelId: string,
): ItemParcelAssignments {
  const result: ItemParcelAssignments = {};
  for (const [key, itemAssignments] of Object.entries(assignments)) {
    const filtered = itemAssignments.filter((a) => a.parcelId !== parcelId);
    if (filtered.length > 0) {
      result[key] = filtered;
    }
  }
  return result;
}

/**
 * Reassign an item's full quantity to a specific parcel (or unassign if null).
 */
export function reassignItemToParcel(
  assignments: ItemParcelAssignments,
  externalId: string,
  parcelId: string | null,
  fallbackQuantity: number,
): ItemParcelAssignments {
  const result = { ...assignments };
  if (parcelId === null) {
    delete result[externalId];
  } else {
    const currentTotal = (result[externalId] ?? []).reduce(
      (sum, a) => sum + a.quantity,
      0,
    );
    const quantity = currentTotal > 0 ? currentTotal : fallbackQuantity;
    result[externalId] = [{ parcelId, quantity }];
  }
  return result;
}

/**
 * Convert a packing parcel to an OutboundParcel for shipping.
 * Uses the parcel's preset if set, otherwise falls back to the template.
 */
export function packingParcelToOutboundParcel(
  packingParcel: PackingParcel,
  fallbackTemplate: OutboundParcel | null,
): OutboundParcel | null {
  if (packingParcel.parcel) {
    return {
      ...packingParcel.parcel,
      parcelAndContentWeight: null,
      insurance: null,
      customs: null,
      dryIceWeight: null,
      hazmat: null,
    };
  }
  return fallbackTemplate ? structuredClone(fallbackTemplate) : null;
}

/**
 * Format a parcel's dimensions for display on the packing panel.
 */
export function getPackingParcelDimensionLabel(parcel: Parcel): string {
  if (parcel.parcelType === ParcelType.CUSTOM) {
    return `${parcel.length} x ${parcel.width} x ${parcel.height} ${parcel.lengthUnit}`;
  }
  return (
    parcel.carrier + (parcel.description ? ` - ${parcel.description}` : "")
  );
}

/**
 * Check if all items with the given external IDs have at least one parcel assignment.
 */
export function allItemsAssignedToParcels(
  itemExternalIds: string[],
  assignments: ItemParcelAssignments,
): boolean {
  return itemExternalIds.every((id) => (assignments[id]?.length ?? 0) > 0);
}

export interface BoxContext {
  parcelId: string;
  boxNumber: number;
  totalBoxes: number;
  boxLabel: string;
  boxBarcodeValue: string;
}

/**
 * Compute the box-context for a specific parcel within a fulfillment group.
 *
 * `boxBarcodeValue` is the scannable payload we print on per-box labels so
 * customs brokers and warehouse ops can tie a physical box back to its FG.
 * The "<fgId>-<boxNumber>" shape is FG-scoped but human-parseable — unlike
 * the raw nanoid parcel id, you can read the FG id off the label.
 *
 * Returns `null` when the parcelId is not found in packingParcels — e.g. when
 * the caller passes a stale id after parcels were reset. The renderer should
 * treat that as "no box context" rather than guessing.
 */
export function getBoxContextForParcel(
  packingParcels: readonly PackingParcel[],
  parcelId: string,
  fulfillmentGroupId: string,
): BoxContext | null {
  const index = packingParcels.findIndex((p) => p.id === parcelId);
  if (index === -1) {
    return null;
  }
  const parcel = packingParcels[index]!;
  const boxNumber = index + 1;
  return {
    parcelId,
    boxNumber,
    totalBoxes: packingParcels.length,
    boxLabel: parcel.label,
    boxBarcodeValue: `${fulfillmentGroupId}-${boxNumber}`,
  };
}
